"use strict";
/**
 * Copyright 2017 Google Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __extends = (this && this.__extends) || (function () {
 var extendStatics = Object.setPrototypeOf ||
 ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
 function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
 return function (d, b) {
 extendStatics(d, b);
 function __() { this.constructor = d; }
 d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
 };
})();
Object.defineProperty(exports, "__esModule", { value: true });
var app_1 = require("@firebase/app");
var util_1 = require("@firebase/util");
var util_2 = require("@firebase/util");
var util_3 = require("@firebase/util");
var util_4 = require("./util/util");
var Path_1 = require("./util/Path");
var VisibilityMonitor_1 = require("./util/VisibilityMonitor");
var OnlineMonitor_1 = require("./util/OnlineMonitor");
var util_5 = require("@firebase/util");
var Connection_1 = require("../realtime/Connection");
var util_6 = require("@firebase/util");
var util_7 = require("@firebase/util");
var ServerActions_1 = require("./ServerActions");
var RECONNECT_MIN_DELAY = 1000;
var RECONNECT_MAX_DELAY_DEFAULT = 60 * 5 * 1000; // 5 minutes in milliseconds (Case: 1858)
var RECONNECT_MAX_DELAY_FOR_ADMINS = 30 * 1000; // 30 seconds for admin clients (likely to be a backend server)
var RECONNECT_DELAY_MULTIPLIER = 1.3;
var RECONNECT_DELAY_RESET_TIMEOUT = 30000; // Reset delay back to MIN_DELAY after being connected for 30sec.
var SERVER_KILL_INTERRUPT_REASON = 'server_kill';
// If auth fails repeatedly, we'll assume something is wrong and log a warning / back off.
var INVALID_AUTH_TOKEN_THRESHOLD = 3;
/**
 * Firebase connection. Abstracts wire protocol and handles reconnecting.
 *
 * NOTE: All JSON objects sent to the realtime connection must have property names enclosed
 * in quotes to make sure the closure compiler does not minify them.
 */
var PersistentConnection = /** @class */ (function (_super) {
 __extends(PersistentConnection, _super);
 /**
 * @implements {ServerActions}
 * @param {!RepoInfo} repoInfo_ Data about the namespace we are connecting to
 * @param {function(string, *, boolean, ?number)} onDataUpdate_ A callback for new data from the server
 * @param onConnectStatus_
 * @param onServerInfoUpdate_
 * @param authTokenProvider_
 * @param authOverride_
 */
 function PersistentConnection(repoInfo_, onDataUpdate_, onConnectStatus_, onServerInfoUpdate_, authTokenProvider_, authOverride_) {
 var _this = _super.call(this) || this;
 _this.repoInfo_ = repoInfo_;
 _this.onDataUpdate_ = onDataUpdate_;
 _this.onConnectStatus_ = onConnectStatus_;
 _this.onServerInfoUpdate_ = onServerInfoUpdate_;
 _this.authTokenProvider_ = authTokenProvider_;
 _this.authOverride_ = authOverride_;
 // Used for diagnostic logging.
 _this.id = PersistentConnection.nextPersistentConnectionId_++;
 _this.log_ = util_4.logWrapper('p:' + _this.id + ':');
 /** @private {Object} */
 _this.interruptReasons_ = {};
 _this.listens_ = {};
 _this.outstandingPuts_ = [];
 _this.outstandingPutCount_ = 0;
 _this.onDisconnectRequestQueue_ = [];
 _this.connected_ = false;
 _this.reconnectDelay_ = RECONNECT_MIN_DELAY;
 _this.maxReconnectDelay_ = RECONNECT_MAX_DELAY_DEFAULT;
 _this.securityDebugCallback_ = null;
 _this.lastSessionId = null;
 /** @private {number|null} */
 _this.establishConnectionTimer_ = null;
 /** @private {boolean} */
 _this.visible_ = false;
 // Before we get connected, we keep a queue of pending messages to send.
 _this.requestCBHash_ = {};
 _this.requestNumber_ = 0;
 /** @private {?{
 * sendRequest(Object),
 * close()
 * }} */
 _this.realtime_ = null;
 /** @private {string|null} */
 _this.authToken_ = null;
 _this.forceTokenRefresh_ = false;
 _this.invalidAuthTokenCount_ = 0;
 _this.firstConnection_ = true;
 _this.lastConnectionAttemptTime_ = null;
 _this.lastConnectionEstablishedTime_ = null;
 if (authOverride_ && !util_7.isNodeSdk()) {
 throw new Error('Auth override specified in options, but not supported on non Node.js platforms');
 }
 _this.scheduleConnect_(0);
 VisibilityMonitor_1.VisibilityMonitor.getInstance().on('visible', _this.onVisible_, _this);
 if (repoInfo_.host.indexOf('fblocal') === -1) {
 OnlineMonitor_1.OnlineMonitor.getInstance().on('online', _this.onOnline_, _this);
 }
 return _this;
 }
 /**
 * @param {!string} action
 * @param {*} body
 * @param {function(*)=} onResponse
 * @protected
 */
 PersistentConnection.prototype.sendRequest = function (action, body, onResponse) {
 var curReqNum = ++this.requestNumber_;
 var msg = { r: curReqNum, a: action, b: body };
 this.log_(util_2.stringify(msg));
 util_3.assert(this.connected_, "sendRequest call when we're not connected not allowed.");
 this.realtime_.sendRequest(msg);
 if (onResponse) {
 this.requestCBHash_[curReqNum] = onResponse;
 }
 };
 /**
 * @inheritDoc
 */
 PersistentConnection.prototype.listen = function (query, currentHashFn, tag, onComplete) {
 var queryId = query.queryIdentifier();
 var pathString = query.path.toString();
 this.log_('Listen called for ' + pathString + ' ' + queryId);
 this.listens_[pathString] = this.listens_[pathString] || {};
 util_3.assert(query.getQueryParams().isDefault() ||
 !query.getQueryParams().loadsAllData(), 'listen() called for non-default but complete query');
 util_3.assert(!this.listens_[pathString][queryId], 'listen() called twice for same path/queryId.');
 var listenSpec = {
 onComplete: onComplete,
 hashFn: currentHashFn,
 query: query,
 tag: tag
 };
 this.listens_[pathString][queryId] = listenSpec;
 if (this.connected_) {
 this.sendListen_(listenSpec);
 }
 };
 /**
 * @param {!{onComplete(),
 * hashFn():!string,
 * query: !Query,
 * tag: ?number}} listenSpec
 * @private
 */
 PersistentConnection.prototype.sendListen_ = function (listenSpec) {
 var _this = this;
 var query = listenSpec.query;
 var pathString = query.path.toString();
 var queryId = query.queryIdentifier();
 this.log_('Listen on ' + pathString + ' for ' + queryId);
 var req = { /*path*/ p: pathString };
 var action = 'q';
 // Only bother to send query if it's non-default.
 if (listenSpec.tag) {
 req['q'] = query.queryObject();
 req['t'] = listenSpec.tag;
 }
 req['h'] = listenSpec.hashFn();
 this.sendRequest(action, req, function (message) {
 var payload = message['d'];
 var status = message['s'];
 // print warnings in any case...
 PersistentConnection.warnOnListenWarnings_(payload, query);
 var currentListenSpec = _this.listens_[pathString] && _this.listens_[pathString][queryId];
 // only trigger actions if the listen hasn't been removed and readded
 if (currentListenSpec === listenSpec) {
 _this.log_('listen response', message);
 if (status !== 'ok') {
 _this.removeListen_(pathString, queryId);
 }
 if (listenSpec.onComplete) {
 listenSpec.onComplete(status, payload);
 }
 }
 });
 };
 /**
 * @param {*} payload
 * @param {!Query} query
 * @private
 */
 PersistentConnection.warnOnListenWarnings_ = function (payload, query) {
 if (payload && typeof payload === 'object' && util_1.contains(payload, 'w')) {
 var warnings = util_1.safeGet(payload, 'w');
 if (Array.isArray(warnings) && ~warnings.indexOf('no_index')) {
 var indexSpec = '".indexOn": "' +
 query
 .getQueryParams()
 .getIndex()
 .toString() +
 '"';
 var indexPath = query.path.toString();
 util_4.warn("Using an unspecified index. Your data will be downloaded and " +
 ("filtered on the client. Consider adding " + indexSpec + " at ") +
 (indexPath + " to your security rules for better performance."));
 }
 }
 };
 /**
 * @inheritDoc
 */
 PersistentConnection.prototype.refreshAuthToken = function (token) {
 this.authToken_ = token;
 this.log_('Auth token refreshed');
 if (this.authToken_) {
 this.tryAuth();
 }
 else {
 //If we're connected we want to let the server know to unauthenticate us. If we're not connected, simply delete
 //the credential so we dont become authenticated next time we connect.
 if (this.connected_) {
 this.sendRequest('unauth', {}, function () { });
 }
 }
 this.reduceReconnectDelayIfAdminCredential_(token);
 };
 /**
 * @param {!string} credential
 * @private
 */
 PersistentConnection.prototype.reduceReconnectDelayIfAdminCredential_ = function (credential) {
 // NOTE: This isn't intended to be bulletproof (a malicious developer can always just modify the client).
 // Additionally, we don't bother resetting the max delay back to the default if auth fails / expires.
 var isFirebaseSecret = credential && credential.length === 40;
 if (isFirebaseSecret || util_5.isAdmin(credential)) {
 this.log_('Admin auth credential detected. Reducing max reconnect time.');
 this.maxReconnectDelay_ = RECONNECT_MAX_DELAY_FOR_ADMINS;
 }
 };
 /**
 * Attempts to authenticate with the given credentials. If the authentication attempt fails, it's triggered like
 * a auth revoked (the connection is closed).
 */
 PersistentConnection.prototype.tryAuth = function () {
 var _this = this;
 if (this.connected_ && this.authToken_) {
 var token_1 = this.authToken_;
 var authMethod = util_5.isValidFormat(token_1) ? 'auth' : 'gauth';
 var requestData = { cred: token_1 };
 if (this.authOverride_ === null) {
 requestData['noauth'] = true;
 }
 else if (typeof this.authOverride_ === 'object') {
 requestData['authvar'] = this.authOverride_;
 }
 this.sendRequest(authMethod, requestData, function (res) {
 var status = res['s'];
 var data = res['d'] || 'error';
 if (_this.authToken_ === token_1) {
 if (status === 'ok') {
 _this.invalidAuthTokenCount_ = 0;
 }
 else {
 // Triggers reconnect and force refresh for auth token
 _this.onAuthRevoked_(status, data);
 }
 }
 });
 }
 };
 /**
 * @inheritDoc
 */
 PersistentConnection.prototype.unlisten = function (query, tag) {
 var pathString = query.path.toString();
 var queryId = query.queryIdentifier();
 this.log_('Unlisten called for ' + pathString + ' ' + queryId);
 util_3.assert(query.getQueryParams().isDefault() ||
 !query.getQueryParams().loadsAllData(), 'unlisten() called for non-default but complete query');
 var listen = this.removeListen_(pathString, queryId);
 if (listen && this.connected_) {
 this.sendUnlisten_(pathString, queryId, query.queryObject(), tag);
 }
 };
 PersistentConnection.prototype.sendUnlisten_ = function (pathString, queryId, queryObj, tag) {
 this.log_('Unlisten on ' + pathString + ' for ' + queryId);
 var req = { /*path*/ p: pathString };
 var action = 'n';
 // Only bother sending queryId if it's non-default.
 if (tag) {
 req['q'] = queryObj;
 req['t'] = tag;
 }
 this.sendRequest(action, req);
 };
 /**
 * @inheritDoc
 */
 PersistentConnection.prototype.onDisconnectPut = function (pathString, data, onComplete) {
 if (this.connected_) {
 this.sendOnDisconnect_('o', pathString, data, onComplete);
 }
 else {
 this.onDisconnectRequestQueue_.push({
 pathString: pathString,
 action: 'o',
 data: data,
 onComplete: onComplete
 });
 }
 };
 /**
 * @inheritDoc
 */
 PersistentConnection.prototype.onDisconnectMerge = function (pathString, data, onComplete) {
 if (this.connected_) {
 this.sendOnDisconnect_('om', pathString, data, onComplete);
 }
 else {
 this.onDisconnectRequestQueue_.push({
 pathString: pathString,
 action: 'om',
 data: data,
 onComplete: onComplete
 });
 }
 };
 /**
 * @inheritDoc
 */
 PersistentConnection.prototype.onDisconnectCancel = function (pathString, onComplete) {
 if (this.connected_) {
 this.sendOnDisconnect_('oc', pathString, null, onComplete);
 }
 else {
 this.onDisconnectRequestQueue_.push({
 pathString: pathString,
 action: 'oc',
 data: null,
 onComplete: onComplete
 });
 }
 };
 PersistentConnection.prototype.sendOnDisconnect_ = function (action, pathString, data, onComplete) {
 var request = { /*path*/ p: pathString, /*data*/ d: data };
 this.log_('onDisconnect ' + action, request);
 this.sendRequest(action, request, function (response) {
 if (onComplete) {
 setTimeout(function () {
 onComplete(response['s'], response['d']);
 }, Math.floor(0));
 }
 });
 };
 /**
 * @inheritDoc
 */
 PersistentConnection.prototype.put = function (pathString, data, onComplete, hash) {
 this.putInternal('p', pathString, data, onComplete, hash);
 };
 /**
 * @inheritDoc
 */
 PersistentConnection.prototype.merge = function (pathString, data, onComplete, hash) {
 this.putInternal('m', pathString, data, onComplete, hash);
 };
 PersistentConnection.prototype.putInternal = function (action, pathString, data, onComplete, hash) {
 var request = {
 /*path*/ p: pathString,
 /*data*/ d: data
 };
 if (hash !== undefined)
 request['h'] = hash;
 // TODO: Only keep track of the most recent put for a given path?
 this.outstandingPuts_.push({
 action: action,
 request: request,
 onComplete: onComplete
 });
 this.outstandingPutCount_++;
 var index = this.outstandingPuts_.length - 1;
 if (this.connected_) {
 this.sendPut_(index);
 }
 else {
 this.log_('Buffering put: ' + pathString);
 }
 };
 PersistentConnection.prototype.sendPut_ = function (index) {
 var _this = this;
 var action = this.outstandingPuts_[index].action;
 var request = this.outstandingPuts_[index].request;
 var onComplete = this.outstandingPuts_[index].onComplete;
 this.outstandingPuts_[index].queued = this.connected_;
 this.sendRequest(action, request, function (message) {
 _this.log_(action + ' response', message);
 delete _this.outstandingPuts_[index];
 _this.outstandingPutCount_--;
 // Clean up array occasionally.
 if (_this.outstandingPutCount_ === 0) {
 _this.outstandingPuts_ = [];
 }
 if (onComplete)
 onComplete(message['s'], message['d']);
 });
 };
 /**
 * @inheritDoc
 */
 PersistentConnection.prototype.reportStats = function (stats) {
 var _this = this;
 // If we're not connected, we just drop the stats.
 if (this.connected_) {
 var request = { /*counters*/ c: stats };
 this.log_('reportStats', request);
 this.sendRequest(/*stats*/ 's', request, function (result) {
 var status = result['s'];
 if (status !== 'ok') {
 var errorReason = result['d'];
 _this.log_('reportStats', 'Error sending stats: ' + errorReason);
 }
 });
 }
 };
 /**
 * @param {*} message
 * @private
 */
 PersistentConnection.prototype.onDataMessage_ = function (message) {
 if ('r' in message) {
 // this is a response
 this.log_('from server: ' + util_2.stringify(message));
 var reqNum = message['r'];
 var onResponse = this.requestCBHash_[reqNum];
 if (onResponse) {
 delete this.requestCBHash_[reqNum];
 onResponse(message['b']);
 }
 }
 else if ('error' in message) {
 throw 'A server-side error has occurred: ' + message['error'];
 }
 else if ('a' in message) {
 // a and b are action and body, respectively
 this.onDataPush_(message['a'], message['b']);
 }
 };
 PersistentConnection.prototype.onDataPush_ = function (action, body) {
 this.log_('handleServerMessage', action, body);
 if (action === 'd')
 this.onDataUpdate_(body['p'], body['d'], 
 /*isMerge*/ false, body['t']);
 else if (action === 'm')
 this.onDataUpdate_(body['p'], body['d'], 
 /*isMerge=*/ true, body['t']);
 else if (action === 'c')
 this.onListenRevoked_(body['p'], body['q']);
 else if (action === 'ac')
 this.onAuthRevoked_(body['s'], body['d']);
 else if (action === 'sd')
 this.onSecurityDebugPacket_(body);
 else
 util_4.error('Unrecognized action received from server: ' +
 util_2.stringify(action) +
 '\nAre you using the latest client?');
 };
 PersistentConnection.prototype.onReady_ = function (timestamp, sessionId) {
 this.log_('connection ready');
 this.connected_ = true;
 this.lastConnectionEstablishedTime_ = new Date().getTime();
 this.handleTimestamp_(timestamp);
 this.lastSessionId = sessionId;
 if (this.firstConnection_) {
 this.sendConnectStats_();
 }
 this.restoreState_();
 this.firstConnection_ = false;
 this.onConnectStatus_(true);
 };
 PersistentConnection.prototype.scheduleConnect_ = function (timeout) {
 var _this = this;
 util_3.assert(!this.realtime_, "Scheduling a connect when we're already connected/ing?");
 if (this.establishConnectionTimer_) {
 clearTimeout(this.establishConnectionTimer_);
 }
 // NOTE: Even when timeout is 0, it's important to do a setTimeout to work around an infuriating "Security Error" in
 // Firefox when trying to write to our long-polling iframe in some scenarios (e.g. Forge or our unit tests).
 this.establishConnectionTimer_ = setTimeout(function () {
 _this.establishConnectionTimer_ = null;
 _this.establishConnection_();
 }, Math.floor(timeout));
 };
 /**
 * @param {boolean} visible
 * @private
 */
 PersistentConnection.prototype.onVisible_ = function (visible) {
 // NOTE: Tabbing away and back to a window will defeat our reconnect backoff, but I think that's fine.
 if (visible &&
 !this.visible_ &&
 this.reconnectDelay_ === this.maxReconnectDelay_) {
 this.log_('Window became visible. Reducing delay.');
 this.reconnectDelay_ = RECONNECT_MIN_DELAY;
 if (!this.realtime_) {
 this.scheduleConnect_(0);
 }
 }
 this.visible_ = visible;
 };
 PersistentConnection.prototype.onOnline_ = function (online) {
 if (online) {
 this.log_('Browser went online.');
 this.reconnectDelay_ = RECONNECT_MIN_DELAY;
 if (!this.realtime_) {
 this.scheduleConnect_(0);
 }
 }
 else {
 this.log_('Browser went offline. Killing connection.');
 if (this.realtime_) {
 this.realtime_.close();
 }
 }
 };
 PersistentConnection.prototype.onRealtimeDisconnect_ = function () {
 this.log_('data client disconnected');
 this.connected_ = false;
 this.realtime_ = null;
 // Since we don't know if our sent transactions succeeded or not, we need to cancel them.
 this.cancelSentTransactions_();
 // Clear out the pending requests.
 this.requestCBHash_ = {};
 if (this.shouldReconnect_()) {
 if (!this.visible_) {
 this.log_("Window isn't visible. Delaying reconnect.");
 this.reconnectDelay_ = this.maxReconnectDelay_;
 this.lastConnectionAttemptTime_ = new Date().getTime();
 }
 else if (this.lastConnectionEstablishedTime_) {
 // If we've been connected long enough, reset reconnect delay to minimum.
 var timeSinceLastConnectSucceeded = new Date().getTime() - this.lastConnectionEstablishedTime_;
 if (timeSinceLastConnectSucceeded > RECONNECT_DELAY_RESET_TIMEOUT)
 this.reconnectDelay_ = RECONNECT_MIN_DELAY;
 this.lastConnectionEstablishedTime_ = null;
 }
 var timeSinceLastConnectAttempt = new Date().getTime() - this.lastConnectionAttemptTime_;
 var reconnectDelay = Math.max(0, this.reconnectDelay_ - timeSinceLastConnectAttempt);
 reconnectDelay = Math.random() * reconnectDelay;
 this.log_('Trying to reconnect in ' + reconnectDelay + 'ms');
 this.scheduleConnect_(reconnectDelay);
 // Adjust reconnect delay for next time.
 this.reconnectDelay_ = Math.min(this.maxReconnectDelay_, this.reconnectDelay_ * RECONNECT_DELAY_MULTIPLIER);
 }
 this.onConnectStatus_(false);
 };
 PersistentConnection.prototype.establishConnection_ = function () {
 if (this.shouldReconnect_()) {
 this.log_('Making a connection attempt');
 this.lastConnectionAttemptTime_ = new Date().getTime();
 this.lastConnectionEstablishedTime_ = null;
 var onDataMessage_1 = this.onDataMessage_.bind(this);
 var onReady_1 = this.onReady_.bind(this);
 var onDisconnect_1 = this.onRealtimeDisconnect_.bind(this);
 var connId_1 = this.id + ':' + PersistentConnection.nextConnectionId_++;
 var self_1 = this;
 var lastSessionId_1 = this.lastSessionId;
 var canceled_1 = false;
 var connection_1 = null;
 var closeFn_1 = function () {
 if (connection_1) {
 connection_1.close();
 }
 else {
 canceled_1 = true;
 onDisconnect_1();
 }
 };
 var sendRequestFn = function (msg) {
 util_3.assert(connection_1, "sendRequest call when we're not connected not allowed.");
 connection_1.sendRequest(msg);
 };
 this.realtime_ = {
 close: closeFn_1,
 sendRequest: sendRequestFn
 };
 var forceRefresh = this.forceTokenRefresh_;
 this.forceTokenRefresh_ = false;
 // First fetch auth token, and establish connection after fetching the token was successful
 this.authTokenProvider_
 .getToken(forceRefresh)
 .then(function (result) {
 if (!canceled_1) {
 util_4.log('getToken() completed. Creating connection.');
 self_1.authToken_ = result && result.accessToken;
 connection_1 = new Connection_1.Connection(connId_1, self_1.repoInfo_, onDataMessage_1, onReady_1, onDisconnect_1, 
 /* onKill= */ function (reason) {
 util_4.warn(reason + ' (' + self_1.repoInfo_.toString() + ')');
 self_1.interrupt(SERVER_KILL_INTERRUPT_REASON);
 }, lastSessionId_1);
 }
 else {
 util_4.log('getToken() completed but was canceled');
 }
 })
 .then(null, function (error) {
 self_1.log_('Failed to get token: ' + error);
 if (!canceled_1) {
 if (util_6.CONSTANTS.NODE_ADMIN) {
 // This may be a critical error for the Admin Node.js SDK, so log a warning.
 // But getToken() may also just have temporarily failed, so we still want to
 // continue retrying.
 util_4.warn(error);
 }
 closeFn_1();
 }
 });
 }
 };
 /**
 * @param {string} reason
 */
 PersistentConnection.prototype.interrupt = function (reason) {
 util_4.log('Interrupting connection for reason: ' + reason);
 this.interruptReasons_[reason] = true;
 if (this.realtime_) {
 this.realtime_.close();
 }
 else {
 if (this.establishConnectionTimer_) {
 clearTimeout(this.establishConnectionTimer_);
 this.establishConnectionTimer_ = null;
 }
 if (this.connected_) {
 this.onRealtimeDisconnect_();
 }
 }
 };
 /**
 * @param {string} reason
 */
 PersistentConnection.prototype.resume = function (reason) {
 util_4.log('Resuming connection for reason: ' + reason);
 delete this.interruptReasons_[reason];
 if (util_1.isEmpty(this.interruptReasons_)) {
 this.reconnectDelay_ = RECONNECT_MIN_DELAY;
 if (!this.realtime_) {
 this.scheduleConnect_(0);
 }
 }
 };
 PersistentConnection.prototype.handleTimestamp_ = function (timestamp) {
 var delta = timestamp - new Date().getTime();
 this.onServerInfoUpdate_({ serverTimeOffset: delta });
 };
 PersistentConnection.prototype.cancelSentTransactions_ = function () {
 for (var i = 0; i < this.outstandingPuts_.length; i++) {
 var put = this.outstandingPuts_[i];
 if (put && /*hash*/ 'h' in put.request && put.queued) {
 if (put.onComplete)
 put.onComplete('disconnect');
 delete this.outstandingPuts_[i];
 this.outstandingPutCount_--;
 }
 }
 // Clean up array occasionally.
 if (this.outstandingPutCount_ === 0)
 this.outstandingPuts_ = [];
 };
 /**
 * @param {!string} pathString
 * @param {Array.<*>=} query
 * @private
 */
 PersistentConnection.prototype.onListenRevoked_ = function (pathString, query) {
 // Remove the listen and manufacture a "permission_denied" error for the failed listen.
 var queryId;
 if (!query) {
 queryId = 'default';
 }
 else {
 queryId = query.map(function (q) { return util_4.ObjectToUniqueKey(q); }).join('$');
 }
 var listen = this.removeListen_(pathString, queryId);
 if (listen && listen.onComplete)
 listen.onComplete('permission_denied');
 };
 /**
 * @param {!string} pathString
 * @param {!string} queryId
 * @return {{queries:Array., onComplete:function(string)}}
 * @private
 */
 PersistentConnection.prototype.removeListen_ = function (pathString, queryId) {
 var normalizedPathString = new Path_1.Path(pathString).toString(); // normalize path.
 var listen;
 if (this.listens_[normalizedPathString] !== undefined) {
 listen = this.listens_[normalizedPathString][queryId];
 delete this.listens_[normalizedPathString][queryId];
 if (util_1.getCount(this.listens_[normalizedPathString]) === 0) {
 delete this.listens_[normalizedPathString];
 }
 }
 else {
 // all listens for this path has already been removed
 listen = undefined;
 }
 return listen;
 };
 PersistentConnection.prototype.onAuthRevoked_ = function (statusCode, explanation) {
 util_4.log('Auth token revoked: ' + statusCode + '/' + explanation);
 this.authToken_ = null;
 this.forceTokenRefresh_ = true;
 this.realtime_.close();
 if (statusCode === 'invalid_token' || statusCode === 'permission_denied') {
 // We'll wait a couple times before logging the warning / increasing the
 // retry period since oauth tokens will report as "invalid" if they're
 // just expired. Plus there may be transient issues that resolve themselves.
 this.invalidAuthTokenCount_++;
 if (this.invalidAuthTokenCount_ >= INVALID_AUTH_TOKEN_THRESHOLD) {
 // Set a long reconnect delay because recovery is unlikely
 this.reconnectDelay_ = RECONNECT_MAX_DELAY_FOR_ADMINS;
 // Notify the auth token provider that the token is invalid, which will log
 // a warning
 this.authTokenProvider_.notifyForInvalidToken();
 }
 }
 };
 PersistentConnection.prototype.onSecurityDebugPacket_ = function (body) {
 if (this.securityDebugCallback_) {
 this.securityDebugCallback_(body);
 }
 else {
 if ('msg' in body && typeof console !== 'undefined') {
 console.log('FIREBASE: ' + body['msg'].replace('\n', '\nFIREBASE: '));
 }
 }
 };
 PersistentConnection.prototype.restoreState_ = function () {
 var _this = this;
 //Re-authenticate ourselves if we have a credential stored.
 this.tryAuth();
 // Puts depend on having received the corresponding data update from the server before they complete, so we must
 // make sure to send listens before puts.
 util_1.forEach(this.listens_, function (pathString, queries) {
 util_1.forEach(queries, function (key, listenSpec) {
 _this.sendListen_(listenSpec);
 });
 });
 for (var i = 0; i < this.outstandingPuts_.length; i++) {
 if (this.outstandingPuts_[i])
 this.sendPut_(i);
 }
 while (this.onDisconnectRequestQueue_.length) {
 var request = this.onDisconnectRequestQueue_.shift();
 this.sendOnDisconnect_(request.action, request.pathString, request.data, request.onComplete);
 }
 };
 /**
 * Sends client stats for first connection
 * @private
 */
 PersistentConnection.prototype.sendConnectStats_ = function () {
 var stats = {};
 var clientName = 'js';
 if (util_6.CONSTANTS.NODE_ADMIN) {
 clientName = 'admin_node';
 }
 else if (util_6.CONSTANTS.NODE_CLIENT) {
 clientName = 'node';
 }
 stats['sdk.' + clientName + '.' + app_1.default.SDK_VERSION.replace(/\./g, '-')] = 1;
 if (util_7.isMobileCordova()) {
 stats['framework.cordova'] = 1;
 }
 else if (util_7.isReactNative()) {
 stats['framework.reactnative'] = 1;
 }
 this.reportStats(stats);
 };
 /**
 * @return {boolean}
 * @private
 */
 PersistentConnection.prototype.shouldReconnect_ = function () {
 var online = OnlineMonitor_1.OnlineMonitor.getInstance().currentlyOnline();
 return util_1.isEmpty(this.interruptReasons_) && online;
 };
 /**
 * @private
 */
 PersistentConnection.nextPersistentConnectionId_ = 0;
 /**
 * Counter for number of connections created. Mainly used for tagging in the logs
 * @type {number}
 * @private
 */
 PersistentConnection.nextConnectionId_ = 0;
 return PersistentConnection;
}(ServerActions_1.ServerActions));
exports.PersistentConnection = PersistentConnection;

//# sourceMappingURL=PersistentConnection.js.map



//////////////////
// WEBPACK FOOTER
// ../database/dist/cjs/src/core/PersistentConnection.js
// module id = 50
// module chunks = 0